import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import { Alert } from "components/base/Toast";
import { useModals } from "ModalProvider";
import { useContext } from "react";
import { PiTrashBold } from "react-icons/pi";
import { DELETE_SHIPMENT_INVOICE } from "./graphql";
import ShipmentContext from "../ShipmentContext";

const InvoiceRow = ({ shipmentInvoice }) => {
  const { dispatch } = useContext(ShipmentContext);
  const modal = useModals();
  const [deleteShipmentInvoice] = useMutation(DELETE_SHIPMENT_INVOICE, {
    onCompleted: (data) => {
      Alert("success", "Shipment Invoice deleted");
      dispatch({
        type: "deleteShipmentInvoice",
        payload: data.deleteShipmentInvoice,
      });
    },
    onError: (error) => {
      Alert("error", parseError(error));
    },
  });
  const reports = shipmentInvoice.qcReports || [];
  const hasQcReport = reports.length > 0;
  const sourceLabel =
    shipmentInvoice.invoice.odooModelType === "transfer" ? "INT" : "SO";

  const getStatusClassName = (status) => {
    const value = (status || "").toUpperCase();
    if (value === "PASS") return "text-green-600";
    if (value === "PENDING") return "text-yellow-600";
    return "text-red-600";
  };

  const showQcReports = () => {
    modal.present({
      title: `QC Reports - ${sourceLabel} ${shipmentInvoice.invoice.number}`,
      center: true,
      children: (
        <div className="space-y-3">
          {reports.length === 0 ? (
            <div className="text-sm text-gray-600 dark:text-gray-300">
              No QC reports found for this {sourceLabel}.
            </div>
          ) : (
            <div className="max-h-[50vh] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">Report</th>
                    <th className="text-left px-3 py-2">Item / Product</th>
                    <th className="text-right px-3 py-2">Qty</th>
                    <th className="text-left px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => {
                    const productText =
                      `[${report.productNumber || "-"}] ${report.productName || "-"}`.trim();
                    return (
                      <tr
                        key={report.id}
                        className="border-t border-gray-100 dark:border-gray-700"
                      >
                        <td className="px-3 py-2 whitespace-nowrap">
                          <a
                            href={`/qc/reports/${report.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
                          >
                            #{report.id}
                          </a>
                        </td>
                        <td className="px-3 py-2">
                          <div
                            className="max-w-[360px] truncate"
                            title={productText}
                          >
                            {productText}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {report.qty}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span
                            className={`font-semibold ${getStatusClassName(report.status)}`}
                          >
                            {(report.status || "PENDING").toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ),
    });
  };

  return (
    <div className="flex justify-between items-start lg:items-center px-4 lg:px-6 py-3 bg-gray-200 dark:bg-gray-800">
      <div className="break-all flex items-baseline">
        <div className="text-base font-bold">
          {sourceLabel} {shipmentInvoice.invoice.number}
        </div>
        {shipmentInvoice.pos.length > 0 ? (
          <div className="ml-2 text-gray-600 dark:text-gray-300">
            PO{shipmentInvoice.pos.map((i) => i.number).join(",")}
          </div>
        ) : null}
        <Button
          onClick={showQcReports}
          className={`ml-6 text-xs font-semibold underline underline-offset-2 ${
            hasQcReport
              ? "!text-green-600 hover:!text-green-700 active:!text-green-800"
              : "!text-red-600 hover:!text-red-700 active:!text-red-800"
          }`}
          title={`View all QC reports under this ${sourceLabel}`}
        >
          {hasQcReport ? "QC Report" : "No QC Report"}
        </Button>
      </div>

      <Button
        danger
        icon={<PiTrashBold size={16} />}
        aria-label="Delete Invoice"
        title="Delete Invoice"
        className="rounded-md px-2 py-1 text-xs transition-colors hover:bg-red-100 active:bg-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 dark:hover:bg-red-900/60 dark:active:bg-red-800/70"
        onClick={() => {
          if (
            window.confirm(`Are you sure you want to delete this invoice !?`)
          ) {
            deleteShipmentInvoice({ variables: { id: shipmentInvoice.id } });
          }
        }}
      >
        Delete Invoice
      </Button>
    </div>
  );
};

export default InvoiceRow;
